import React, { useId, useState, useRef } from "react";
import styles from "./AvatarCircle.module.scss";

interface AvatarProps {
  image: string | null; // Aktuelles Bild in base64
  setImage: (value: string | null) => void; // Funktion zum Aktualisieren des Bildes
  handleImageRemove?: () => void; // Optionale Funktion zum Entfernen des Bildes
  size?: "small" | "medium" | "large"; // Avatar-Größe (optional)
}

const AvatarCircle: React.FC<AvatarProps> = ({
  image,
  setImage,
  handleImageRemove,
  size = "medium", // Standardmäßig mittlere Größe
}) => {
  const id = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Behandlung des Bild-Uploads
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(file);
    }
  };

  // Dateiverarbeitung
  const processFile = (file: File) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        setImage(reader.result as string);
      }
    };

    reader.onerror = () => {
      console.error("Fehler beim Lesen der Datei");
    };

    reader.readAsDataURL(file);
  };

  // Handler für Drag and Drop
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        processFile(file);
      }
    }
  };

  // Dateiauswahldialog beim Klick auf den Avatar öffnen
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={styles.avatarContainer}>
      <div
        className={`${styles.avatar} ${styles[size]} ${isDragging ? styles.dragging : ""}`}
        onClick={handleAvatarClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {image ? (
          <>
            <img
              src={image}
              alt="Benutzeravatar"
              className={styles.avatarImage}
            />
            <div className={styles.avatarOverlay}>
              <div className={styles.avatarOverlayContent}>
                <span>Ändern</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.placeholder}>
              <svg
                viewBox="0 0 24 24"
                width="40"
                height="40"
                fill="#757575"
                aria-hidden="true"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
            <div className={styles.avatarOverlay}>
              <div className={styles.avatarOverlayContent}>
                <span>Hochladen</span>
              </div>
            </div>
          </>
        )}
      </div>

      {image && handleImageRemove && (
        <button
          className={styles.removeButton}
          onClick={handleImageRemove}
          aria-label="Avatar entfernen"
        >
          <span className={styles.removeIcon}>×</span>
        </button>
      )}

      <input
        ref={fileInputRef}
        id={id}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className={styles.inputFile}
        aria-label="Bild hochladen"
      />
    </div>
  );
};

export default AvatarCircle;
